import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import { IecTask } from '../tasks/iec-task';
import { TaskResponse } from '../tasks/task-response';
import { IecTaskService } from '../services/iec-task-service';
import { Router } from '@angular/router';
import { PersonLoginService } from '../services/person-login-service';
import { PersonLogin, RoleName } from '../person/person-login';
import { TaskResponseService } from '../services/task-response-service';
import { HttpErrorResponse } from '@angular/common/http';
import { StringUtils } from '../utils/string-utils';
import { Entity } from '../entity/entity';
import { EntityService } from '../services/entity-service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FpaTaskData } from '../model/fpa-task-data';
import { firstValueFrom } from 'rxjs';
import { UserApplicationService } from '../services/user-application-service';
import { UserApplication } from '../person/user-application';
import { Organization } from '../organization/organization';
import { OrganizationService } from '../services/organization-service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-task-response',
  templateUrl: './task-response.component.html',
  styleUrls: ['./task-response.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner, DatePipe, CurrencyPipe, FormsModule],
})
export class TaskResponseComponent implements OnInit, OnChanges {
  readonly taskIdParam = input.required<string>({ alias: 'taskId' });
  private readonly denyNotesTaskNames: string[] = ['Riser Data Review'];
  private readonly taskId = signal<number>(undefined!);
  private readonly personLogin = signal<PersonLogin>(undefined!);
  private readonly iecTaskService = inject(IecTaskService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly taskResponseService = inject(TaskResponseService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly userApplicationService = inject(UserApplicationService);
  private readonly organizationService = inject(OrganizationService);
  readonly task = signal<IecTask>(undefined!);
  response!: TaskResponse;
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly taskMessage = signal<string>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  readonly assignedTo = signal<string>(undefined!);
  readonly fpaTaskData = signal<FpaTaskData>(undefined!);
  readonly userApplication = signal<UserApplication>(undefined!);
  readonly userApplicationOrganization = signal<Organization>(undefined!);
  readonly userApplicationParentOrganization = signal<Organization>(undefined!);
  denyNotes!: string;

  private loadTaskDetails(): void {
    this.isBusy.set(true);
    this.initializeTaskData().then(
      () => (this.isBusy.set(false)),
      (error: HttpErrorResponse) => this.handleError(error),
    );
  }

  private async initializeTaskData(): Promise<void> {
    this.task.set(await firstValueFrom<IecTask>(this.iecTaskService.getTask(this.taskId())));
    if (!this.task()) {
      this.taskMessage.set('No task was found with ID ' + this.taskId());
      this.entity.set(undefined!);
      this.personLogin.set(undefined!);
    } else if (this.task().endTime) {
      this.taskMessage.set('This task has been closed, you can close this window or use the links above to go to a different screen');
      this.task.set(undefined!);
      this.entity.set(undefined!);
      this.personLogin.set(undefined!);
    } else {
      this.assignedTo.set(await firstValueFrom<string>(this.iecTaskService.getAssignedTo(this.task().assignee)));
      if (StringUtils.isNotBlank(this.task().entityId)) {
        const entityPattern = new RegExp(/^((QT|MR|SR|R)\d{6,7})$/);
        if (entityPattern.test(this.task().entityId)) {
          const entityName: string = this.task().entityId.startsWith('QT') ? 'QUOTE' : 'CONTRACT';
          this.entity.set(await firstValueFrom<Entity>(this.entityService.getEntity(entityName, this.task().entityId, this.task().entityRevision)));
        }
        if (StringUtils.equalsIgnoreCase('FPA Requested', this.task().name)) {
          await this.router.navigate(['fpaTaskResponse', this.task().id], { replaceUrl: true });
        } else if (StringUtils.equalsIgnoreCase('User Application', this.task().name)) {
          this.userApplication.set(await firstValueFrom<UserApplication>(this.userApplicationService.getAdditionalTaskData(this.task().id)));
          if (StringUtils.isNotBlank(this.userApplication()?.company)) {
            this.userApplicationOrganization.set(await firstValueFrom<Organization>(this.organizationService.getOrganization(this.userApplication().company)));
            if (this.userApplicationOrganization() && StringUtils.isNotBlank(this.userApplicationOrganization().parentOrg)) {
              const parentOrgId: string = this.userApplicationOrganization().parentOrg;
              this.userApplicationParentOrganization.set(await firstValueFrom<Organization>(this.organizationService.getOrganization(parentOrgId)));
            }
          }
        }
      }
    }
  }

  canCloseTask(): boolean {
    if (!this.task()) {
      return false;
    }
    if (!this.personLogin()) {
      return false;
    }
    return this.isCloseAccessAllowed();
  }

  showDenyNotes(): boolean {
    if (!this.task()) {
      return false;
    }
    return this.denyNotesTaskNames.some((name) => StringUtils.equalsIgnoreCase(name, this.task().name));
  }

  canDeny(): boolean {
    return !this.showDenyNotes() || StringUtils.isNotBlank(this.denyNotes);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']).then(() => (this.isBusy.set(false)));
    } else {
      this.personLogin.set(this.personLoginService.getCachedLogin()!);
      this.loadTask();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['taskIdParam'].firstChange) {
      this.loadTask();
    }
  }

  private loadTask(): void {
    const id: string = this.taskIdParam();
    if (id && !isNaN(+id)) {
      this.taskId.set(+id);
      this.loadTaskDetails();
    } else {
      this.taskMessage.set('Cannot find task information with an ID of ' + id);
    }
  }

  closeTask(isApproved: boolean): void {
    if (this.task()) {
      if (!this.isCloseAccessAllowed()) {
        this.taskMessage.set('You do not have permission to close this task');
      }
      this.isBusy.set(true);
      this.response = new TaskResponse();
      this.response.taskId = this.taskId();
      this.response.responder = this.personLogin();
      this.response.approved = isApproved;
      if (!isApproved) {
        this.response.additionalData = this.denyNotes;
      }
      this.taskResponseService.closeTask(this.response).subscribe({
        next: () => {
          this.isBusy.set(false);
          this.loadTaskDetails();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private isCloseAccessAllowed() {
    if (!this.task()) {
      return true;
    }
    if (this.personLoginService.hasRole(RoleName.IECIT)) {
      return true;
    }
    if (this.task().name === 'SN Bypass Rejection Follow-up' && this.personLoginService.hasRole(RoleName.PartsEngineer)) {
      return true;
    }
    return this.personLogin().token === this.task().assignee || this.personLogin().groups.filter((group) => group.token === this.task().assignee).length > 0;
  }

  getEntityUrl(): string {
    if (this.entity()) {
      const pageName: string = this.entity().id.entityName === 'QUOTE' ? 'quotes' : 'orders';
      return pageName + '/' + this.entity().id.entityId + '/' + this.entity().id.entityRevision;
    }
    return '';
  }

  showRiserChartLink(): boolean {
    if (!this.task()) {
      return false;
    }
    const taskNames: string[] = ['Riser Data Review'];
    return taskNames.some((name) => StringUtils.equalsIgnoreCase(name, this.task().name));
  }

  getRiserChartUrl(): string {
    if (this.entity()) {
      return 'riserChart/' + this.entity().id.entityName + '/' + this.entity().id.entityId + '/' + this.entity().id.entityRevision;
    }
    return '';
  }

  getProjectDefaultsUrl(): string {
    if (this.entity()) {
      return 'projectDefaults/' + this.entity().id.entityName + '/' + this.entity().id.entityId + '/' + this.entity().id.entityRevision;
    }
    return '';
  }
}
